import { useState, useRef, useEffect } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useTenants } from '../../lib/api/hooks.js';

/**
 * Which tenant the operator is acting on.
 *
 * This is what makes one codebase serve both audiences (ADR-019). A tenant user
 * has exactly one tenant and never sees this control; a platform or reseller
 * operator picks one and every tenant-scoped screen -- extensions, call history,
 * billing -- follows.
 *
 * It is a UI convenience, not a security boundary: the server re-derives the
 * caller's permitted tenant set on every request and RLS constrains the query
 * independently. Choosing a tenant here cannot grant access to it.
 */
export function TenantSwitcher() {
  const session = useSession((s) => s.session);
  const activeTenantId = useSession((s) => s.activeTenantId);
  const setActiveTenant = useSession((s) => s.setActiveTenant);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const multiTenant = (session?.scope.all_tenants ?? false) || (session?.scope.tenant_ids.length ?? 0) > 1;
  const { data } = useTenants({ limit: 200 });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Default to the first tenant once the list arrives, so tenant-scoped pages
  // are not stuck on "no tenant selected" for a platform admin.
  useEffect(() => {
    if (!activeTenantId && data?.data?.[0]) setActiveTenant(data.data[0].id);
  }, [activeTenantId, data, setActiveTenant]);

  if (!multiTenant) return null;

  const active = data?.data.find((t) => t.id === activeTenantId);
  const list = (data?.data ?? []).filter(
    (t) => !filter || t.name.toLowerCase().includes(filter.toLowerCase())
        || t.slug.includes(filter.toLowerCase()),
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="arc-btn arc-btn-sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ minWidth: 190, justifyContent: 'space-between' }}
      >
        <span className="arc-truncate" style={{ maxWidth: 150 }}>
          {active ? active.name : 'Select tenant…'}
        </span>
        <span aria-hidden="true" style={{ opacity: .6 }}>▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="arc-card"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 60,
            width: 320, maxHeight: 400, overflow: 'auto', boxShadow: 'var(--arc-shadow-lg)',
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid var(--arc-border)', position: 'sticky', top: 0, background: 'var(--arc-surface)' }}>
            <input
              className="arc-input"
              placeholder="Filter tenants…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
              aria-label="Filter tenants"
            />
          </div>
          {list.length === 0 && <div className="arc-empty" style={{ padding: 24 }}>No matches</div>}
          {list.map((t) => (
            <button
              key={t.id}
              type="button"
              role="option"
              aria-selected={t.id === activeTenantId}
              className="arc-nav-item"
              style={{
                width: '100%', border: 'none', background: t.id === activeTenantId ? 'var(--arc-accent-subtle)' : 'none',
                color: 'var(--arc-fg)', borderRadius: 0, padding: '9px 12px', textAlign: 'left',
              }}
              onClick={() => { setActiveTenant(t.id); setOpen(false); setFilter(''); }}
            >
              <div className="arc-stack" style={{ minWidth: 0 }}>
                <span style={{ fontWeight: 550 }} className="arc-truncate">{t.name}</span>
                <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{t.sip_domain}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
